import { parseDataFromError } from "@server/utils/parseDataFromError";
import { trimGraphqlQuery } from "@server/utils/trimGraphqlQuery";
import { getOperationAST, type DocumentNode } from "graphql";

import { CART_COOKIE } from "@/config/cart";
import { env } from "@/env";
import { deleteCookie } from "cookies-next";

type GraphQLVariables = Record<string, unknown>;
type GraphQLResponse<T> = { data: T; errors?: any[] };

const CART_ERROR_CODES = ["Gone", "Not Found"];

function getOperationName(query: DocumentNode): string | undefined {
  return getOperationAST(query)?.name?.value;
}

function endpointWithOperationName(
  endpoint: string,
  operationName?: string,
): string {
  if (!operationName) {
    return endpoint;
  }

  const separator = endpoint.includes("?") ? "&" : "?";
  return `${endpoint}${separator}operationName=${encodeURIComponent(operationName)}`;
}

type RequestOptions = {
  endpoint?: string;
  headers?: HeadersInit;
  next?: NextFetchRequestConfig;
  preview?: boolean;
};

type Request<V> = {
  query: DocumentNode;
  variables?: V;
  options?: RequestOptions;
};

async function throwForNonJsonBody(
  httpResponse: Response,
  operationName?: string,
): Promise<never> {
  const body = await parseDataFromError(httpResponse);
  const message = typeof body === "string" ? body : "Unable to retrieve data";
  throw new Error(operationName ? `[${operationName}] ${message}` : message);
}

function unwrapGraphQLResponse<T>(
  response: GraphQLResponse<T>,
  operationName?: string,
): T {
  if (
    response.errors?.some(
      (error) =>
        CART_ERROR_CODES.includes(error.message) &&
        (error.path?.includes("cart") || error.path?.includes("checkout")),
    )
  ) {
    deleteCookie(CART_COOKIE);
    throw new Error("CART_NOT_FOUND");
  }

  if (!response.data) {
    const message = response.errors?.map((error) => error.message).join(", ");
    throw new Error(operationName ? `[${operationName}] ${message}` : message);
  }

  return response.data;
}

export default async function makeRequest<
  T,
  V extends GraphQLVariables = GraphQLVariables,
>({ query: query, variables, options }: Request<V>): Promise<T> {
  if (!options?.endpoint) {
    throw new Error("Missing endpoint in makeRequest");
  }

  // Disable next cache and revalidation when preview mode is enabled
  const next = options.preview
    ? {
        revalidate: 0,
      }
    : options.next;

  const operationName = getOperationName(query);

  const httpResponse = await fetch(
    endpointWithOperationName(options.endpoint, operationName),
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": options.preview
          ? "no-cache, no-store, must-revalidate"
          : "s-maxage=60, stale-while-revalidate=86400",
        ...options.headers,
      },
      body: JSON.stringify({
        query: trimGraphqlQuery(query),
        variables,
        operationName,
      }),
      next,
    },
  );

  const contentType = httpResponse.headers.get("Content-Type");
  const isJson = Boolean(contentType?.includes("application/json"));

  if (!httpResponse.ok) {
    if (!isJson) {
      return throwForNonJsonBody(httpResponse, operationName);
    }

    const response: GraphQLResponse<T> = await httpResponse.json();
    if (!response.errors?.length) {
      const message = `Request failed: ${httpResponse.status}`;
      throw new Error(
        operationName ? `[${operationName}] ${message}` : message,
      );
    }

    return unwrapGraphQLResponse(response, operationName);
  }

  if (!isJson) {
    return throwForNonJsonBody(httpResponse, operationName);
  }

  const response: GraphQLResponse<T> = await httpResponse.json();
  return unwrapGraphQLResponse(response, operationName);
}

class GraphClient {
  request = async <T, V extends GraphQLVariables>(
    args: Request<V>,
  ): Promise<T> => {
    return await makeRequest<T, V>(args);
  };

  contentful = async <T, V extends GraphQLVariables>(
    args: Request<V>,
  ): Promise<T> => {
    if (!env.CONTENTFUL_SPACE_ID) {
      throw new Error("Missing Contentful space ID");
    }

    if (!env.CONTENTFUL_SPACE_ENVIRONMENT) {
      throw new Error("Missing Contentful space environment");
    }

    if (!process.env.CONTENTFUL_SPACE_TOKEN) {
      throw new Error("Missing Contentful space token");
    }

    return this.request<T, V>({
      ...args,
      options: {
        ...args.options,
        endpoint: `https://graphql.contentful.com/content/v1/spaces/${env.CONTENTFUL_SPACE_ID}/environments/${env.CONTENTFUL_SPACE_ENVIRONMENT}`,
        headers: {
          ...args.options?.headers,
          Authorization: `Bearer ${args.options?.preview ? process.env.CONTENTFUL_SPACE_PREVIEW_TOKEN : process.env.CONTENTFUL_SPACE_TOKEN}`,
        },
      },
    });
  };
}

export const client = new GraphClient();

export const contentful = client.contentful.bind(client);
export const request = client.request.bind(client);

export * from "./queries/contentful";
